import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useState } from "react";

type ApiSummary = {
  range: string;
  percent: number;
  count: number;
};

type OverviewItem = {
  range: string;
  percent: number;
  count: number;
};

type BpSummaryData = {
  bp_summary: ApiSummary[];
  overview: OverviewItem[];
};

type RangeConfig = {
  key: string;
  color: string;
  label: string;
};

const rangesConfig: RangeConfig[] = [
  { key: "<100", color: "#1677ff", label: "<100" },
  { key: "100-109", color: "#52c41a", label: "100-109" },
  { key: "110-119", color: "#52c41a", label: "110-119" },
  { key: "120-129", color: "#13c2c2", label: "120-129" },
  { key: "130-139", color: "#faad14", label: "130-139" },
  { key: "140-149", color: "#fa8c16", label: "140-149" },
  { key: "150-169", color: "#ff4d4f", label: "150-169" },
  { key: ">170", color: "#a8071a", label: ">170" }
];

export const BpSummaryReport = ({
  bpSummaryData
}: {
  bpSummaryData: BpSummaryData;
}) => {
  if (
    !bpSummaryData?.bp_summary ||
    !Array.isArray(bpSummaryData.bp_summary) ||
    bpSummaryData.bp_summary.length === 0
  )
    return null;
  const [copied, setCopied] = useState(false);

  const summary = rangesConfig.map(config => {
    const api = bpSummaryData.bp_summary?.find(r => r.range === config.key);

    return {
      ...config,
      percentage: api ? api.percent : 0,
      count: api?.count ?? 0
    };
  });

  const total = summary.reduce((sum, r) => sum + r.count, 0);

  const highestRange =
    summary.reduce((prev, curr) => (curr.count > prev.count ? curr : prev)) ??
    summary[0];

  const overviewData = bpSummaryData.overview ?? [];

  // ---- COPY FUNCTION ----
  const handleCopy = () => {
    const formatted = `
Blood Pressure Summary
Total Readings: ${total}

Range      Percentage     Count
--------------------------------------------
${summary
  .map(
    r =>
      `${r.label.padEnd(20)} ${String(r.percentage + "%").padEnd(16)} ${
        r.count
      }`
  )
  .join("\n")}

Overview
--------------------------------------------
${overviewData
  .map(
    r =>
      `${r.range.padEnd(20)} ${String(r.percent + "%").padEnd(16)} ${r.count}`
  )
  .join("\n")}
`.trim();

    navigator.clipboard.writeText(formatted);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  return (
    <div
      style={{
        borderRadius: 16,
        padding: 12,
        marginTop: 10,
        marginBottom: 10,
        background: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)"
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Blood Pressure Summary
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            Total Readings: {total}
          </div>
        </div>

        <Button
          onClick={handleCopy}
          size="small"
          style={{
            border: "1px solid #d9d9d9",
            background: copied ? "#f6ffed" : "#fff",
            borderRadius: 6,
            cursor: "pointer",
            color: copied ? "#52c41a" : "inherit",
            transition: "all 0.3s ease"
          }}
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "2px"
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600 }}>Sys BP Range</div>
        <div style={{ fontSize: 12, fontWeight: 600 }}>% Readings</div>
      </div>

      {/* Ranges */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {summary.map(item => {
          const isDominant = item.key === highestRange.key;

          return (
            <div key={item.key}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                  marginBottom: 6
                }}
              >
                <span style={{ color: item.color }}>{item.label}</span>
                <span>
                  {item.percentage}% ({item.count})
                </span>
              </div>

              <div
                style={{
                  height: 4,
                  background: "#f0f0f0",
                  borderRadius: 6,
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    width: `${item.percentage}%`,
                    background: item.color,
                    height: "100%",
                    transition: "width 0.5s ease"
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Overview Section */}
      <div
        style={{
          marginTop: 12,
          padding: 8,
          background: "#f5f5f5",
          borderRadius: 8
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#888",
            marginBottom: 8,
            textAlign: "center",
            fontWeight: 600
          }}
        >
          Overview
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 20
          }}
        >
          {overviewData.map((item, index) => (
            <React.Fragment key={item.range}>
              <div
                style={{
                  textAlign: "center",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center"
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: item.range === "<130" ? "#52c41a" : "#ff4d4f",
                    marginBottom: 8
                  }}
                >
                  {item.range}
                </div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                  {item.percent}%
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {item.count} readings
                </div>
              </div>
              {index < overviewData.length - 1 && (
                <div
                  style={{
                    width: 1,
                    height: 50,
                    background: "#ddd"
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
